import React, { useEffect, createContext, useState } from 'react';
import { Box, LinearProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import { ITeam, IUser } from '../interfaces/IUser';
import KeycloakService from '../services/KeycloakService';
import DatahubAPI from '../api/DatahubAPI';
import UsersAPI from 'api/UsersAPI';
import GroupAPI from 'api/GroupAPI';
import PaymentsAPI from 'api/PaymentsAPI';
import keycloakService from '../services/KeycloakService';

const proMonthlyPrice = process.env.REACT_APP_BITE_PRO_MONTHLY!;

interface IGlobalState {
  cur_user: IUser;
  userOpts: any[];
  handleTeamChange: any;
  initKeycloakUser: any;
}

const empty_usr: IUser = {
  basics: undefined,
  teams: [],
  isAdmin: false,
  selected_team: { id: 'EMPTY_TEAM', name: 'EMPTY_TEAM', path: 'EMPTY_TEAM' },
  bite_super_admin: false,
  service_account: false,
  modules: {},
  stripe_cus_info: [],
  awsUser: false,
};

const initialState: IGlobalState = {
  cur_user: empty_usr,
  userOpts: [],

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  handleTeamChange: () => { },
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  initKeycloakUser: () => { },
};

export const GlobalContext = createContext(initialState);

interface IGlobalProviderProps {
  children?: JSX.Element | JSX.Element[];
}

export const GlobalProvider: React.FC<IGlobalProviderProps> = ({
  children,
}) => {
  const queryParameters = new URLSearchParams(window.location.search);

  const openRoute = window.location.pathname.includes('submit-questionnaire');

  const awsUserAccessToken = queryParameters.get('accessToken');
  const awsUserRefreshToken = queryParameters.get('refreshToken');
  if (awsUserAccessToken && awsUserRefreshToken) {
    localStorage.setItem('awsUserAccessToken', awsUserAccessToken);
    localStorage.setItem('awsUserRefreshToken', awsUserRefreshToken);
  }
  const awsUserTokenPayload = {
    accessToken: localStorage.getItem('awsUserAccessToken'),
    refreshToken: localStorage.getItem('awsUserRefreshToken'),
  };
  const { enqueueSnackbar } = useSnackbar();
  const [ userOpts, setUserOpts ] = useState<{
    isLoading: boolean;
    error: string | undefined;
    data: any[];
  }>({ isLoading: true, error: undefined, data: [] });
  const [ curUser, setCurUser ] = useState<{
    isLoading: boolean;
    error: string | undefined;
    data: IUser;
  }>({ isLoading: true, error: undefined, data: empty_usr });

  const getUsersForTeam = async(teamId: string) => {
    UsersAPI.getUsers(teamId)?.then((res) => {
      if (res?.success) {
        const user_opts = res.result?.map((u: any) => ({
          label: u.email,
          value: u.id,
        }));
        setUserOpts({ isLoading: false, error: undefined, data: user_opts });
      } else {
        const err_text = `Error loading Training Users: ${res?.result}`;
        enqueueSnackbar(err_text, { variant: 'error' });
        setUserOpts({ isLoading: false, error: err_text, data: [] });
      }
    });
  };

  const initKeycloakUser = async() => {
    try {
      const successCallback = async(user: any) => {
        const resp = await UsersAPI.getUser(user.id);
        if (resp?.success) {
          const thisUser = resp.result;
          thisUser.service_account = await KeycloakService.isServiceAccount();
          if (thisUser.service_account) {
            KeycloakService.doLogout();
            // eslint-disable-next-line no-alert
            alert('Service accounts do not have application level access');
            return;
          }
          thisUser.teams.map(async(team: any, i: any) => {
            const res = await GroupAPI.getGroupRealmRoles(team.id);
            if (res.success) {
              if (
                res.result.find((obj: any) => {
                  return obj.name === 'archived';
                })
              ) {
                thisUser.teams.splice(i, 1);
              } else {
                const teamWithMembers = team;
                const getMembers = await UsersAPI.getUsers(team.id);
                if (getMembers?.success) {
                  teamWithMembers.members = getMembers?.result;
                  const getAttributes = await GroupAPI.getGroupById(team.id);
                  if (getAttributes?.success) {
                    teamWithMembers.attributes =
                      getAttributes?.result?.attributes;
                  }
                }
              }
            }
          });
          // update lastAccess attribute
          const lastAccess = Date.now();
          const stripeRes = await PaymentsAPI.getCustomerByEmail(
            thisUser.basics.email,
          );
          if (stripeRes.success) {
            thisUser.stripe_cus_info = stripeRes.result.data;
            thisUser.bite_super_admin = await KeycloakService.isBiteAdmin();
            if (thisUser.teams.length > 0) {
              thisUser.selected_team =
                thisUser.teams[
                  JSON.parse(
                    sessionStorage.getItem('curr_team') === '-1' ?
                      '0' :
                      sessionStorage.getItem('curr_team') || '0',
                  )
                ];
              const teamAttributes = await GroupAPI.getGroupById(
                thisUser.selected_team.id,
              );
              thisUser.selected_team.attributes =
                teamAttributes?.result.attributes;

              await shouldStartMobileUserWebTrial(
                thisUser.basics.id,
                thisUser.stripe_cus_info?.[0]?.id,
                {
                  id: thisUser.selected_team.id,
                  admin: thisUser?.basics?.email,
                  name: thisUser.selected_team.name,
                }
              );
            }
            await DatahubAPI.writeToIndex({
              user_id: thisUser?.basics?.id,
              user_email: thisUser?.basics?.email,
              firstName: thisUser?.basics?.firstName ?
                thisUser?.basics?.firstName :
                'N/A',
              lastName: thisUser?.basics?.lastName ?
                thisUser?.basics?.lastName :
                'N/A',
              lastAccess: lastAccess,
              firstLogin: thisUser?.basics?.attributes?.terms_and_conditions[0] ?
                thisUser?.basics?.attributes?.terms_and_conditions[0] :
                '',
            });
          }
          setCurUser({ isLoading: false, error: undefined, data: thisUser });
          getUsersForTeam(thisUser.selected_team?.id);
          sessionStorage.setItem(
            'curr_team',
            JSON.stringify(thisUser.teams.indexOf(thisUser.selected_team)),
          );
        } else {
          const error_msg = `Failed pulling info for your user - ${resp?.result}`;
          enqueueSnackbar(error_msg, { variant: 'error' });
          setCurUser({ isLoading: false, error: error_msg, data: empty_usr });
        }
      };
      const awsSuccessCallback = async(user: any) => {
        const resp = await UsersAPI.getUser(user.id);
        if (resp?.success) {
          const thisUser = resp.result;
          thisUser.service_account = await KeycloakService.isServiceAccount();
          if (thisUser.service_account) {
            KeycloakService.doLogout();
            // eslint-disable-next-line no-alert
            alert('Service accounts do not have application level access');
            return;
          }
          thisUser.teams.map(async(team: any, i: any) => {
            const res = await GroupAPI.getGroupRealmRoles(team.id);
            if (res.success) {
              if (
                res.result.find((obj: any) => {
                  return obj.name === 'archived';
                })
              ) {
                thisUser.teams.splice(i, 1);
              } else {
                const teamWithMembers = team;
                const getMembers = await UsersAPI.getUsers(team.id);
                if (getMembers?.success) {
                  teamWithMembers.members = getMembers?.result;
                  const getAttributes = await GroupAPI.getGroupById(team.id);
                  if (getAttributes?.success) {
                    teamWithMembers.attributes =
                      getAttributes?.result?.attributes;
                  }
                }
              }
            }
          });
          thisUser.awsUser = true;
          thisUser.bite_super_admin = await KeycloakService.isBiteAdmin();
          if (thisUser.teams.length > 0) {
            thisUser.selected_team =
              thisUser.teams[
                JSON.parse(
                  sessionStorage.getItem('curr_team') === '-1' ?
                    '0' :
                    sessionStorage.getItem('curr_team') || '0',
                )
              ];
            const teamAttributes = await GroupAPI.getGroupById(
              thisUser.selected_team.id,
            );
            thisUser.selected_team.attributes =
              teamAttributes?.result.attributes;
          }
          setCurUser({ isLoading: false, error: undefined, data: thisUser });
          getUsersForTeam(thisUser.selected_team?.id);
          sessionStorage.setItem(
            'curr_team',
            JSON.stringify(thisUser.teams.indexOf(thisUser.selected_team)),
          );
        } else {
          const error_msg = `Failed pulling info for your user - ${resp?.result}`;
          enqueueSnackbar(error_msg, { variant: 'error' });
          setCurUser({ isLoading: false, error: error_msg, data: empty_usr });
        }
      };
      if (awsUserTokenPayload.accessToken && awsUserTokenPayload.refreshToken) {
        await KeycloakService.initKeycloak(
          awsSuccessCallback,
          awsUserTokenPayload,
        );
      } else {
        await KeycloakService.initKeycloak(successCallback, null);
      }
    } catch (e: any) {
      const error_msg = `Failed pulling info for your user - ${e.toString()}`;
      enqueueSnackbar(error_msg, { variant: 'error' });
      setCurUser({ isLoading: false, error: error_msg, data: empty_usr });
    }
  };

  const handleTeamChange = async(newTeam: ITeam) => {
    // const modules = await GroupAPI.getGroupRealmRoles(newTeam.id);
    const attributes = await GroupAPI.getGroupById(newTeam.id);
    newTeam.attributes = attributes?.result?.attributes;
    const newUser = {
      ...curUser.data,
      selected_team: newTeam,
    };
    setCurUser({ isLoading: false, error: undefined, data: newUser });
    getUsersForTeam(newTeam.id);
    sessionStorage.setItem(
      'curr_team',
      JSON.stringify(newUser.teams.indexOf(newUser.selected_team)),
    );
  };

  const shouldStartMobileUserWebTrial = async(
    user_id: string,
    cus_id: string,
    teamObj: any
  ) => {
    PaymentsAPI.shouldStartMobileUserWebTrial(user_id, cus_id, proMonthlyPrice, teamObj)?.then((res) => {
      if (res?.success) {
        if (res.result?.startedTrial === true){
          keycloakService.doLogin();
        }
      } else if (cus_id) {
        const err_text = 'Error checking mobile/web trial';
        enqueueSnackbar(err_text, { variant: 'error' });
        setUserOpts({ isLoading: false, error: err_text, data: [] });
      }
    });
  };

  useEffect(() => {
    if (!openRoute) {
      initKeycloakUser();
    } else {
      setCurUser({
        data: empty_usr,
        isLoading: false,
        error: undefined,
      });
    }
  }, []);

  const state_val: IGlobalState = {
    handleTeamChange: handleTeamChange,
    initKeycloakUser: initKeycloakUser,
    userOpts: userOpts.data,
    cur_user: curUser.data,
  };

  if (curUser.isLoading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <GlobalContext.Provider value={state_val}>
      {children}
    </GlobalContext.Provider>
  );
};
